import { Lock, Crown, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePremium } from "@/hooks/usePremium";
import { useLang } from "@/hooks/use-lang";

interface PremiumPaywallProps {
  variant?: "inline" | "overlay";
}

const PremiumPaywall = ({ variant = "inline" }: PremiumPaywallProps) => {
  const { upgrade, remainingPlans, isPremium } = usePremium();
  const { lang } = useLang();

  if (isPremium) return null;

  const features = lang === "fr"
    ? [
        "Planning jour par jour détaillé",
        "Recommandations optimisées",
        "Liste complète de restaurants & activités",
        "Plans de voyage illimités",
      ]
    : [
        "Detailed day-by-day plan",
        "Optimized recommendations",
        "Full restaurant & activity list",
        "Unlimited travel plans",
      ];

  return (
    <div className="relative">
      {/* Gradient fade overlay */}
      {variant === "overlay" && (
        <div className="absolute -top-20 left-0 right-0 h-20 bg-gradient-to-t from-background to-transparent pointer-events-none z-10" />
      )}

      <div className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-primary/10 p-6 space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              {lang === "fr" ? "Débloquer l'itinéraire complet" : "Unlock your full itinerary"}
              <Crown className="w-5 h-5 text-primary" />
            </h3>
            <p className="text-sm text-muted-foreground">
              {lang === "fr"
                ? `${remainingPlans} plan${remainingPlans !== 1 ? "s" : ""} gratuit${remainingPlans !== 1 ? "s" : ""} restant${remainingPlans !== 1 ? "s" : ""}`
                : `${remainingPlans} free plan${remainingPlans !== 1 ? "s" : ""} remaining`}
            </p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-2.5">
          {features.map((feature, i) => (
            <div key={i} className="flex items-center gap-2.5 text-sm">
              <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <Check className="w-3 h-3 text-primary" />
              </div>
              <span className="text-foreground/80">{feature}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <Button
          onClick={upgrade}
          className="w-full h-13 rounded-2xl text-base font-bold gap-2 bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
          size="lg"
        >
          <Sparkles className="w-5 h-5" />
          {lang === "fr" ? "Passer à Premium" : "Upgrade to Premium"}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          {lang === "fr"
            ? "Accès instantané • Annulation à tout moment"
            : "Instant access • Cancel anytime"}
        </p>
      </div>
    </div>
  );
};

export default PremiumPaywall;
